import { useEffect, useRef, useState } from 'react';
import { useSocket } from './useSocket';
import { io } from 'socket.io-client';

export interface ScanProgressData {
    scanId: number;
    progress: number;
    status: string;
    timestamp: string;
    message?: string;
    runId?: number;
    workflowUrl?: string;
    workflowStatus?: string;
    workflowConclusion?: string;
    downloadUrl?: string;
    error?: string;
}

export interface ScanMonitorHook {
    isConnected: boolean;
    scanProgress: Record<number, ScanProgressData>;
    subscribeTo: (scanId: number, runId?: number) => void;
    unsubscribeFrom: (scanId: number) => void;
    clearAll: () => void;
}

export function useScanMonitor(socketUrl?: string): ScanMonitorHook {
    const [isConnected, setIsConnected] = useState(false);
    const [scanProgress, setScanProgress] = useState<Record<number, ScanProgressData>>({});
    let socket = useSocket();
    const subscribedScansRef = useRef<Set<number>>(new Set());
    const subscriptionRetryRef = useRef<Map<number, number>>(new Map());
    // useSocket() hands back a brand-new socket instance whenever it tears down and
    // reconnects (e.g. the active workgroup changed, re-authenticating under a different
    // JWT). When that happens, any scan subscriptions/progress tracked below belong to the
    // PREVIOUS connection's workgroup context and must be dropped rather than silently
    // replayed onto the new one.
    const previousSocketRef = useRef<typeof socket>(null);
    const MAX_RETRY_ATTEMPTS = 5;
    const RETRY_DELAY = 5000;

    useEffect(() => {
        if (!socket) return;

        if (previousSocketRef.current && previousSocketRef.current !== socket) {
            subscribedScansRef.current.clear();
            subscriptionRetryRef.current.clear();
            setScanProgress({});
            setIsConnected(false);
        }
        previousSocketRef.current = socket;

        socket.on('connect', () => {
            setIsConnected(true);

            subscribedScansRef.current.forEach(scanId => {
                socket.emit('subscribeScan', { scanId });
                subscriptionRetryRef.current.delete(scanId);
            });
        });

        socket.on('disconnect', (reason) => {
            setIsConnected(false);
        });

        socket.on('scanProgress', (data: ScanProgressData) => {
            setScanProgress(prev => ({
                ...prev,
                [data.scanId]: data
            }));
        });

        socket.on('scanSubscribed', (data) => {
            subscriptionRetryRef.current.delete(data.scanId);
        });

        socket.on('scanUnsubscribed', (data) => {
        });

        socket.on('connect_error', (error) => {
            setIsConnected(false);
        });

        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, [socket]);

    const subscribeTo = (scanId: number, runId?: number) => {

        if (!socket) {
            subscribedScansRef.current.add(scanId);
            subscriptionRetryRef.current.set(scanId, 0);

            setTimeout(() => {
                attemptSubscriptionRetry(scanId, runId);
            }, 1000);
            return;
        }

        if (socket.connected) {
            socket.emit('subscribeScan', { scanId, runId });
            subscribedScansRef.current.add(scanId);

            setTimeout(() => {
                if (!subscriptionRetryRef.current.has(scanId)) {
                    subscriptionRetryRef.current.set(scanId, 0);
                    attemptSubscriptionRetry(scanId, runId);
                }
            }, 20000);
        } else {
            subscribedScansRef.current.add(scanId);
            subscriptionRetryRef.current.set(scanId, 0);

            if (socket.disconnected) {
                socket.connect();
            }

            setTimeout(() => {
                attemptSubscriptionRetry(scanId, runId);
            }, 20000);
        }
    };

    const attemptSubscriptionRetry = (scanId: number, runId?: number) => {
        const attempts = subscriptionRetryRef.current.get(scanId) || 0;

        if (attempts >= MAX_RETRY_ATTEMPTS) {
            subscriptionRetryRef.current.delete(scanId);
            return;
        }

        if (socket?.connected) {
            socket.emit('subscribeScan', { scanId, runId });
            subscriptionRetryRef.current.set(scanId, attempts + 1);

        } else {
            if (!socket) {
                subscriptionRetryRef.current.delete(scanId);
                return;
            } else {
                if (socket.disconnected) {
                    socket.connect();
                }

                setTimeout(() => {
                    if (subscriptionRetryRef.current.has(scanId)) {
                        attemptSubscriptionRetry(scanId, runId);
                    }
                }, RETRY_DELAY);
            }
        }
    };

    const unsubscribeFrom = (scanId: number) => {
        if (socket?.connected) {
            socket.emit('unsubscribeScan', { scanId });
        } else if (socket) {
            if (socket.disconnected) {
                socket.connect();
                setTimeout(() => {
                    if (socket.connected) {
                        socket.emit('unsubscribeScan', { scanId });
                    }
                }, 1000);
            }
        }

        subscribedScansRef.current.delete(scanId);
        subscriptionRetryRef.current.delete(scanId);

        setScanProgress(prev => {
            const updated = { ...prev };
            delete updated[scanId];
            return updated;
        });
    };

    const clearAll = () => {
        subscribedScansRef.current.forEach(scanId => {
            if (socket?.connected) {
                socket.emit('unsubscribeScan', { scanId });
            }
        });
        subscribedScansRef.current.clear();
        subscriptionRetryRef.current.clear();
        setScanProgress({});
    };

    return {
        isConnected,
        scanProgress,
        subscribeTo,
        unsubscribeFrom,
        clearAll
    };
}